import clsx from 'clsx'
import { ComponentProps, ElementType } from 'react'

import { DesignIcon, isSystemIcon } from '@/shared/lib/icons'
import { Icon } from '@/shared/ui/icon'

import { LoadingCircle } from '../../LoadingCircle'

import cl from './Button.module.scss'

export type ButtonOwnProps<E extends ElementType = 'button'> = {
	as?: E
	theme?: 'primary' | 'secondary' | 'outlined' | 'outlined-primary' | 'link' | 'clear'
	size?: 'S' | 'M' | 'L'
	fullWidth?: boolean
	isLoading?: boolean
	startIcon?: DesignIcon
	endIcon?: DesignIcon
}

export type ButtonProps<E extends ElementType = 'button'> = ButtonOwnProps<E> &
	Omit<ComponentProps<E>, keyof ButtonOwnProps<E>>

export const Button = <E extends ElementType = 'button'>(props: ButtonProps<E>) => {
	const {
		as,
		theme = 'primary',
		size = 'M',
		startIcon,
		endIcon,
		disabled,
		fullWidth,
		isLoading,
		children,
		className,
		block: _block,
		...otherProps
	} = props

	const Component = as ?? 'button'
	const isNativeButton = Component === 'button'
	const loadingColor =
		theme === 'primary' ? '#fff' : theme === 'outlined-primary' || theme === 'link' ? '#568dfe' : '#1f2227'

	const iconClasses = (icon: string) =>
		clsx(cl.icon, {
			[cl.icon__system]: isSystemIcon(icon)
		})

	return (
		<Component
			className={clsx(cl.root, cl[theme], cl[size], className, {
				[cl.disabled]: disabled,
				[cl.loading]: isLoading,
				[cl.fullWidth]: fullWidth
			})}
			{...(isNativeButton ? { disabled } : {})}
			{...otherProps}>
			<span className={clsx(cl.content, { [cl.content__loading]: isLoading })}>
				{startIcon && <Icon name={startIcon} className={iconClasses(startIcon)} />}
				{children}
				{endIcon && <Icon name={endIcon} className={iconClasses(endIcon)} />}
			</span>
			{isLoading && <LoadingCircle className={cl.loadingIndicator} size={16} color={loadingColor} />}
		</Component>
	)
}
